import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, type ColDef } from 'ag-grid-community';
import { useNavigate } from 'react-router';
import { Button, Typography } from '@equinor/eds-core-react';
import moment from 'moment';
import {
  FetchAntonyms, FetchSynonyms, FetchTopics, PollSyncStatus, SyncTopics,
} from '../store/topics/actions';
import { useMountEffect } from '../containers/App';
import RootState from '../store';
import { Topic } from '../store/topics/types';
import { TopicsTooltip } from './TopicsTooltip';

import { DEFAULT_TOPIC_SCORE, TIMEOUT_QUEUEMSG_PICKUP } from '../constants';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './TopicsOverview.css';
import { CustomConfirm } from './CustomConfirm';

ModuleRegistry.registerModules([AllCommunityModule]);

export function TopicsOverview() {
  const navigate = useNavigate();

  const boolToHumanActiveFlag = (params: { value: string | boolean; }) => ((params.value === true || params.value === 'true') ? 'Active' : 'Inactive');

  const dateFormatter = (params:any) => moment(params.value).format('DD/MM/YYYY');

  const rawTooltip = (params: any) => params.value;

  const [columnDefs] = useState<ColDef[]>([
    {
      field: 'topicId',
      headerName: 'Id',
      width: 69,
      resizable: true,
      sortable: true,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 100,
      resizable: true,
      valueFormatter: boolToHumanActiveFlag,
      sortable: true,
    },
    {
      field: 'name',
      headerName: 'Topic name',
      width: 280,
      resizable: true,
      sortable: true,
    },
    {
      field: 'definition',
      tooltip: rawTooltip,
      width: 300,
      resizable: true,
      sortable: false,
    },
    {
      field: 'synonyms',
      headerName: 'Synonyms terms',
      tooltip: rawTooltip,
      width: 285,
      resizable: true,
      sortable: false,
    },
    {
      field: 'antonyms',
      headerName: 'Antonyms terms',
      tooltip: rawTooltip,
      width: 285,
      resizable: true,
      sortable: false,
    },
    {
      field: 'experienceScore',
      tooltip: rawTooltip,
      headerTooltip: 'Experience score',
      headerName: 'Experience score',
      width: 120,
      resizable: true,
      sortable: true,
      cellClass: 'hover-column',
    },
    {
      field: 'incidentScore',
      tooltip: rawTooltip,
      headerTooltip: 'Incident score',
      headerName: 'Incident score',
      width: 120,
      resizable: true,
      sortable: true,
      cellClass: 'hover-column',
    },
    {
      field: 'operationScore',
      tooltip: rawTooltip,
      headerTooltip: 'Operation score',
      headerName: 'Operation score',
      width: 120,
      resizable: true,
      sortable: true,
      cellClass: 'hover-column',
    },
    {
      field: 'operationSummaryScore',
      tooltip: rawTooltip,
      headerTooltip: 'Operation summary score',
      headerName: 'Operation summary score',
      width: 120,
      resizable: true,
      sortable: true,
      cellClass: 'hover-column',
    },
    {
      field: 'sectionSummaryScore',
      tooltip: rawTooltip,
      headerTooltip: 'Section summary score',
      headerName: 'Section summary score',
      width: 120,
      resizable: true,
      sortable: true,
      cellClass: 'hover-column',
    },
    {
      field: 'dailyReportScore',
      tooltip: rawTooltip,
      headerTooltip: 'Daily report score',
      headerName: 'Daily report score',
      width: 120,
      resizable: true,
      sortable: true,
      cellClass: 'hover-column',
    },
    {
      field: 'comments',
      tooltip: rawTooltip,
      width: 260,
      resizable: true,
      sortable: false,
    },
    {
      field: 'updateDate',
      headerName: 'Update date',
      tooltip: rawTooltip,
      valueFormatter: dateFormatter,
      width: 200,
      resizable: true,
      sortable: true,
    },
  ]);

  const defaultColDef = useMemo(() => ({
    filter: true,
    cellDataType: false,
    tooltipComponent: TopicsTooltip,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  }), []);

  const gridRef = useRef<AgGridReact>(null);

  const dispatch = useDispatch();
  useMountEffect(() => {
    dispatch(FetchTopics());
    dispatch(FetchSynonyms());
    dispatch(FetchAntonyms());
    dispatch(PollSyncStatus());
  });

  const state = useSelector((state: RootState) => state);
  const { topicState } = state;
  const [disableSync, setDisableSync] = useState(false);

  const onAddClick = (_event: any) => {
    const dts = DEFAULT_TOPIC_SCORE;
    const topic: Topic = new Topic(0, false, '', '', '', dts, dts, dts, dts, dts, dts, [], []);

    void navigate(
      '/topic',
      {
        state: {
          state,
          topic,
          activity: 'new',
        },
      },
    );
  };

  const onRowClick = (event: any) => {
    void navigate(
      '/topic',
      {
        state: {
          state,
          topic: event.data,
          activity: 'edit',
        },
      },
    );
  };

  const gridOptions = {
    rowClassRules: {
      'row-disabled': (params: any) => params.data.isActive === false,
    },
  };

  const syncSelect = () => {
    CustomConfirm(
      'Apply changes?',
      'This will update topics in report search index',
      () => {
        dispatch(SyncTopics());
        setDisableSync(true);
        setTimeout(() => setDisableSync(false), TIMEOUT_QUEUEMSG_PICKUP);
      },
      () => { },
    );
  };

  const SYNC_WAIT = 'Please wait...';
  const SYNC_NO_CHANGES = 'Topics are up to date.';
  const SYNC_NOT_READY = 'Transformation in progress...';

  return (
    <div>
      <Typography variant="h1" style={{ textAlign: 'center' }}>Topics management</Typography>

      <div id="topics-grid" className="ag-theme-alpine">

        <Button variant="outlined" onClick={onAddClick}>Add topic</Button>

        <Button
          disabled={!state.topicSyncState.hasChange || !state.topicSyncState.isReady || disableSync}
          variant="outlined"
          onClick={syncSelect}
          title={disableSync ? SYNC_WAIT : !state.topicSyncState.hasChange ? SYNC_NO_CHANGES : !state.topicSyncState.isReady ? SYNC_NOT_READY : ''}
        >
          Apply changes
        </Button>

        <AgGridReact
          ref={gridRef} // Ref for accessing Grid's API
          rowData={topicState.topics}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows
          rowSelection={{ mode: 'singleRow', checkboxes: false, enableClickSelection: true }}
          suppressCellFocus
          theme="legacy"
          tooltipShowDelay={0}
          gridOptions={gridOptions}
          overlayNoRowsTemplate="Loading..."
          columnHoverHighlight
          onRowClicked={(event) => onRowClick(event)}
        />
      </div>

    </div>
  );
}
